import ReactGA from 'react-ga4';

const CONSENT_STORAGE_KEY = 'analytics-consent';

/**
 * Returns whether analytics consent has been granted.
 * Consent state is persisted in localStorage by the consent banner.
 */
export const hasAnalyticsConsent = () => {
  try {
    return window.localStorage.getItem(CONSENT_STORAGE_KEY) === 'granted';
  } catch {
    // Storage unavailable (e.g., privacy mode): fail closed.
    return false;
  }
};

/**
 * Records the user's analytics consent decision.
 * Called by the consent banner; also dispatches any queued initialization.
 */
export const setAnalyticsConsent = (granted) => {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, granted ? 'granted' : 'denied');
  } catch {
    // Storage unavailable: state is not persisted.
  }
  // Keep Google Consent Mode in sync with the banner decision. gtag is
  // defined by index.html; if it has not run yet, the default stays
  // denied and nothing is stored.
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', { analytics_storage: granted ? 'granted' : 'denied' });
  }
  if (granted) {
    ensureAnalyticsInitialized();
  }
};

let initialized = false;

// Analytics must never run outside production: dev servers, CI and E2E
// runs would otherwise report into the production GA4 property.
// REACT_APP_GA_FORCE=true overrides the host check (used by unit tests).
const analyticsHostEnabled = () =>
  window.location.hostname === 'www.wecobook.com' ||
  process.env.REACT_APP_GA_FORCE === 'true';

/**
 * Initializes GA4 exactly once, only after consent is granted and only on
 * the production host.
 */
export const ensureAnalyticsInitialized = () => {
  if (initialized || !hasAnalyticsConsent()) return;
  if (!analyticsHostEnabled()) return;
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!measurementId) return;
  ReactGA.initialize(measurementId);
  initialized = true;
};

/**
 * Tracks a GA4 event with the given name and parameters.
 * Events are dropped unless consent has been granted.
 * 
 * Event glossary (for internal documentation):
 * - 'indicator_search': Triggered when user searches for an economic indicator.
 *   Purpose: Measure usage of indicator search feature.
 *   Allowed parameters: query_length (number).
 *   Retention: As per GA4 settings.
 *   Consent: Requires analytics consent.
 * 
 * - 'comparison_created': Triggered when user creates a country/indicator comparison.
 *   Purpose: Measure usage of comparison tool.
 *   Allowed parameters: country_count (number).
 *   Retention: As per GA4 settings.
 *   Consent: Requires analytics consent.
 * 
 * - 'export_interest': Triggered when user clicks export button (before actual export).
 *   Purpose: Measure interest in data export feature.
 *   Allowed parameters: scope (string: 'country' | 'comparison'), country_count (number).
 *   Retention: As per GA4 settings.
 *   Consent: Requires analytics consent.
 * 
 * - 'chart_interaction': Triggered when user interacts with a chart (e.g., zoom, tooltip).
 *   Purpose: Measure engagement with visualizations.
 *   Allowed parameters: chartType (string), action (string: 'zoom', 'tooltip', etc.).
 *   Retention: As per GA4 settings.
 *   Consent: Requires analytics consent.
 * 
 * - 'offer_exposure': Triggered when user is shown a monetization offer (e.g., upsell to Pro).
 *   Purpose: Measure reach of monetization experiments.
 *   Allowed parameters: offerId (string), offerType (string: 'saved_watchlist', 'alerts', etc.).
 *   Retention: As per GA4 settings.
 *   Consent: Requires analytics consent.
 *
 * - 'map_country_selected': Triggered when user clicks a country on the world map.
 *   Purpose: Measure engagement with the interactive map.
 *   Allowed parameters: country_code (string), indicator_type (string).
 *   Retention: As per GA4 settings.
 *   Consent: Requires analytics consent.
 *
 * - 'guide_read': Triggered when a guide page is opened.
 *   Purpose: Measure guide content consumption.
 *   Allowed parameters: guide_path (string).
 *   Retention: As per GA4 settings.
 *   Consent: Requires analytics consent.
 */

/**
 * Tracks a GA4 event with the given name and parameters.
 * @param {string} eventName - The name of the event (must match one in the glossary).
 * @param {Object} [params] - Optional parameters to send with the event.
 */
export const trackEvent = (eventName, params = {}) => {
  ensureAnalyticsInitialized();
  if (!hasAnalyticsConsent() || !initialized) return;
  try {
    ReactGA.event({ action: eventName, category: 'engagement', ...params });
  } catch (error) {
    console.error('GA4 event tracking failed:', error);
  }
};

/**
 * Sends a page_view hit for the given SPA route. The initial page view from
 * index.html is suppressed (send_page_view: false), so this is the single
 * source of page views and every client-side navigation is measured.
 */
export const trackPageView = (path) => {
  ensureAnalyticsInitialized();
  if (!hasAnalyticsConsent() || !initialized) return;
  try {
    ReactGA.send({ hitType: 'pageview', page: path });
  } catch (error) {
    console.error('GA4 page view tracking failed:', error);
  }
};

const analytics = { trackEvent };
export default analytics;